import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Navbar from 'react-bootstrap/Navbar'
import Row from 'react-bootstrap/Row'
import Spinner from 'react-bootstrap/Spinner'
import User from '@utils/storage/user'
import ExternalAuthentication from '@utils/external-authentication'
import toast from '@utils/toast'
import LOGIN_BY_AUTH_CODE from '@graphql/mutations/login-by-auth-code'
import { t } from '@utils/translations'
import { RETURN_URL_KEY, validateReturnUrl } from '@utils/auth/returnUrl'

const DEFAULT_ROUTE = '/transactions'

function ExternalAuthenticationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loginByAuthCode, { loading, error }] = useMutation(LOGIN_BY_AUTH_CODE, {
    onCompleted(data) {
      User.set({ login: data.loginByAuthCode.login, role: data.loginByAuthCode.role })

      let targetRoute = DEFAULT_ROUTE

      try {
        const storedUrl = sessionStorage.getItem(RETURN_URL_KEY)

        sessionStorage.removeItem(RETURN_URL_KEY)

        if (storedUrl && validateReturnUrl(storedUrl)) {
          targetRoute = storedUrl
        }
      } catch {
        // Silently fall back to default route
      }

      // Replace the history entry so the consumed OAuth code in the callback
      // URL (/authentication/new?code=...) is dropped from the URL and back
      // history and can't be revisited or replayed.
      navigate(targetRoute, { replace: true })
      toast(t('messages.loggedIn'))
    },
    onError() {
      toast(t('messages.authenticationError'), 'danger')
    }
  })

  const hasAttemptedLogin = useRef(false)

  useEffect(() => {
    if (hasAttemptedLogin.current) return

    hasAttemptedLogin.current = true

    const accessCode = searchParams.get('code')
    const codeVerifier = sessionStorage.getItem('codeVerifier')

    // The OAuth authorization code and PKCE verifier are single-use. Clear the
    // verifier immediately so a duplicate mount (e.g. StrictMode) or a later
    // visit can't replay an already-consumed code and trigger a 401.
    sessionStorage.removeItem('codeVerifier')

    loginByAuthCode({
      variables: { accessCode, codeVerifier }
    })
  }, [loginByAuthCode, searchParams])

  return (
    <>
      <Navbar expand='lg' bg='dark' variant='dark' style={{ height: '58px' }}>
        <Container fluid='xl'>
          <Navbar.Brand>BSP Processor</Navbar.Brand>
        </Container>
      </Navbar>
      <Container fluid='xl' className='py-5'>
        { loading && (
          <div className='d-flex justify-content-center'>
            <Spinner animation='border' role='status' className='my-2' />
          </div>
        )}
        { error && (
          <Card>
            <Card.Body className='py-5'>
              <Row>
                <Col md={{ span: 6, offset: 3 }}>
                  <Alert variant='danger' className='alert-danger-light mb-4'>
                    <Alert.Heading>Authentication wasn't successful!</Alert.Heading>
                    <p>{error.message}</p>
                    <hr />
                    <p>Please, try to log in again!</p>
                  </Alert>
                  <div className='d-grid'>
                    <Button
                      variant='success'
                      onClick={() => ExternalAuthentication.redirect()}
                    >
                      Log in
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Container>
    </>
  )
}

export default ExternalAuthenticationPage
